<template>
  <v-form
    ref="theForm"
    @submit.prevent="submit"
    class="text-body-1"
    v-if="!success"
  >
    <v-card
      class="pa-4 bg-grey-darken-4"
      elevation="0"
    >
      <v-card-title>{{ ctaTitle }}</v-card-title>
      <v-card-text>
        {{ ctaSubtitle }}
      </v-card-text>
      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="4"
            class="py-1"
          >
            <CompactTextField
              dark
              label="First Name"
              v-model="model.firstName"
              :rules="rules.firstName"
              counter="20"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
            class="py-1"
          >
            <CompactTextField
              label="Last Name"
              v-model="model.lastName"
              :rules="rules.lastName"
              counter="20"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
            class="py-1"
          >
            <CompactTextField
              label="Business Email"
              v-model="model.email"
              :rules="rules.email"
              counter="256"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
            class="py-1"
          >
            <CompactTextField
              label="Company"
              v-model="model.companyName"
              :rules="rules.companyName"
              counter="32"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
            class="py-1"
          >
            <CompactTextField
              label="Job Title"
              v-model="model.jobTitle"
              :rules="rules.jobTitle"
              counter="32"
              :required="false"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
            class="py-1"
          >
            <v-select
              label="Company Size"
              v-model="model.companySize"
              :items="companySizeOptions"
              item-title="text"
              item-value="value"
              solo
              flat
              dense
              variant="underlined"
              autocomplete="off"
              density="compact"
              :menu-props="{ contentClass: 'company-size-list' }"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-1"
          >
            <p>Message</p>
            <v-textarea
              data-testid="message"
              v-model="model.message"
              :rules="rules.message"
              rows="1"
              solo
              counter="3000"
              flat
              dense
              variant="underlined"
              autocomplete="off"
              density="compact"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn
          color="primary"
          type="submit"
          class="mx-5 mb-4"
          variant="elevated"
          :loading="loading"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
  <span v-else>&nbsp;</span>
</template>

<script lang="ts" setup>
  import { VuetifyForm, ContactLead, CompanySizeOptions, useCtaText } from '@/models';
  import CompactTextField from './CompactTextField.vue';
  import { ref } from 'vue';

  const { ctaTitle, ctaSubtitle } = useCtaText();

  const theForm = ref(null as unknown as VuetifyForm);
  const loading = ref(false);
  const success = ref(false);
  const model = ref({} as ContactLead);
  const companySizeOptions = Object.entries(CompanySizeOptions).map(([key, value]) => ({
    text: key,
    value,
  }));

  const rules = {
    firstName: [
      (v: string) => !!v || 'First name is required.',
      (v: string) => /^[A-Za-z\s-]+$/.test(v) || 'Only letters, dashes, and spaces are allowed.',
      (v: string) => v.length < 20 || 'First name is too long.',
    ],
    lastName: [
      (v: string) => !!v || 'Last ame is required.',
      (v: string) => /^[A-Za-z\s-]+$/.test(v) || 'Only letters, dashes, and spaces are allowed.',
      (v: string) => v.length < 20 || 'First name is too long.',
    ],
    email: [
      (v: string) => !!v || 'Email is required.',
      (v: string) => /.+@.+\..+/.test(v) || 'Must be a valid email address.',
      (v: string) => v.length > 5 || 'Email must be greater than 5 characters.',
      (v: string) => v.length <= 256 || 'Email must is too long.',
    ],
    companyName: [
      (v: string) => !!v || 'Company is required.',
      (v: string) => v.length < 32 || 'Company name is too long.',
    ],
    jobTitle: [(v: string) => !v || v.length < 32 || 'Job title is too long.'],
    message: [
      (v: string) => !v || v.length < 32 || 'Message is too long.',
      (v: string) => !v || v.length <= 3000 || 'Message must is too long.',
    ],
  };

  const emit = defineEmits<{
    (e: 'submitted', payload?: string): void;
  }>();

  function resetFormValues() {
    const { value } = model;
    value.firstName = '';
    value.lastName = '';
    value.email = '';
    value.companyName = '';
    value.jobTitle = '';
    value.companySize = '';
    value.message = '';
  }

  async function submit() {
    const { validate, resetValidation } = theForm.value;
    const validationResults = await validate();
    if (validationResults.valid) {
      const modelValue = model.value;
      const data = {
        firstName: modelValue.firstName,
        lastName: modelValue.lastName,
        email: modelValue.email,
        phone: '1234567890',
        companyName: modelValue.companyName,
        jobTitle: modelValue.jobTitle ?? '<not specified>',
        companySize: modelValue.companySize ?? '<not specified>',
        message: modelValue.message ?? '',
        timeFrame: null,
      };

      loading.value = true;

      try {
        const response = await fetch('/api/contacts/requests', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        emit('submitted');
        setTimeout(() => {
          resetFormValues();
          resetValidation();
        }, 10);
        success.value = true;
      } catch (error) {
        console.log(error);
      } finally {
        loading.value = false;
      }
    } else {
      console.debug('Form did not pass validation.');
    }
  }
</script>

<style lang="scss" scoped>
  $error-color: #db4242;

  :deep(.v-counter) {
  color: white !important;
} 

 :deep(.v-messages__message) {
  font-weight: 800;
  color: $error-color !important;
}

 :deep(.v-text-field .v-field--error:not(.v-field--disabled) .v-label.v-field-label) {
  color: $error-color !important;
 }
 :deep(.v-text-field .v-field--error:not(.v-field-disabled) .v-field__outline) {
//   display: none !important;
color:$error-color !important;
}
</style>
